import { FC, useId } from "react"

import { ReactComponent as IconSearch } from "@common_assets/svg/search.svg"
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import IconWrapper from "components/common/icon/iconWrapper"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"
import { IClassStoreItem } from "ts/interfaces/Class"

import {
  ICartLine,
  MAX_CART_LINES,
  MAX_QUANTITY_PER_LINE,
} from "../SendStoreChargeDialog.utils"
import StoreItemTile from "../storeItemTile"

interface IProps {
  studentCount: number
  search: string
  onSearchChange: (search: string) => void
  // Search and the announced count live in the dialog so Back to items keeps them.
  announcedCount: number | null
  availableCount: number
  items: IClassStoreItem[]
  filteredItems: IClassStoreItem[]
  isLoadingItems: boolean
  isItemsError: boolean
  onRetryItems: () => void
  cart: ICartLine[]
  onAdd: (item: IClassStoreItem) => void
  onDecrement: (itemId: number) => void
  onRemove: (itemId: number) => void
}

const SelectStep: FC<IProps> = ({
  studentCount,
  search,
  onSearchChange,
  announcedCount,
  availableCount,
  items,
  filteredItems,
  isLoadingItems,
  isItemsError,
  onRetryItems,
  cart,
  onAdd,
  onDecrement,
  onRemove,
}) => {
  const { t } = useTranslation()
  const capLinesId = useId()
  const capPerItemId = useId()
  const itemsReady = !isLoadingItems && !isItemsError
  const linesCapReached = cart.length >= MAX_CART_LINES
  const anyLineAtMax = cart.some(
    (line) => line.quantity >= MAX_QUANTITY_PER_LINE,
  )

  return (
    <Stack gap={1.5}>
      {studentCount === 0 && (
        <Typography variant="body2" color="mockup.neutral20">
          {t("storeCharge.emptySelection")}
        </Typography>
      )}
      <Stack direction="row" alignItems="center" gap={2}>
        <TextField
          autoFocus
          size="small"
          fullWidth
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={`${t("store.searchForItem")}...`}
          slotProps={{
            htmlInput: { "aria-label": t("store.searchForItem") },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <IconWrapper>
                    <IconSearch />
                  </IconWrapper>
                </InputAdornment>
              ),
            },
          }}
        />
        {itemsReady && (
          <Typography
            variant="body3"
            color="mockup.neutral20"
            flexShrink={0}
            aria-hidden
          >
            {t("storeCharge.select.available", { count: availableCount })}
          </Typography>
        )}
        <Box aria-live="polite" sx={visuallyHiddenSx}>
          {announcedCount != null &&
            t("storeCharge.select.available", { count: announcedCount })}
        </Box>
      </Stack>
      <Typography variant="body2" fontWeight={700} color="mockup.neutral10">
        {t("storeCharge.select.storeItems")}
      </Typography>
      <Box aria-live="polite">
        {linesCapReached && (
          <Typography id={capLinesId} variant="body3" color="mockup.neutral20">
            {t("storeCharge.select.capLines")}
          </Typography>
        )}
      </Box>
      {/* Fixed height so searching doesn't resize the dialog; padding keeps tile focus rings unclipped. */}
      <Box sx={{ height: "360px", overflowY: "auto", p: 0.5, m: -0.5 }}>
        {isLoadingItems ? (
          <Stack alignItems="center" justifyContent="center" gap={1} py={4}>
            <CircularProgress size="24px" />
            <Typography variant="body3" color="mockup.neutral20">
              {t("storeCharge.select.loading")}
            </Typography>
          </Stack>
        ) : isItemsError ? (
          <Stack alignItems="flex-start" gap={1} py={2}>
            <Typography variant="body2" color="mockup.neutral10">
              {t("storeCharge.select.errorTitle")}
            </Typography>
            <Button variant="outlined" onClick={onRetryItems}>
              {t("storeCharge.select.tryAgain")}
            </Button>
          </Stack>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="mockup.neutral20" py={2}>
            {t("storeCharge.select.emptyNoItems")}
          </Typography>
        ) : filteredItems.length === 0 ? (
          <Typography variant="body2" color="mockup.neutral20" py={2}>
            {t("storeCharge.select.emptyNoMatch", { query: search.trim() })}
          </Typography>
        ) : (
          <Grid container spacing={1.5}>
            {filteredItems.map((item) => {
              const quantity =
                cart.find((line) => line.item.id === item.id)?.quantity ?? 0
              const blockedReasonId =
                quantity >= MAX_QUANTITY_PER_LINE
                  ? capPerItemId
                  : quantity === 0 && linesCapReached
                    ? capLinesId
                    : undefined
              return (
                <Grid key={item.id} size={{ mobile: 6, desktop: 4 }}>
                  <StoreItemTile
                    item={item}
                    quantity={quantity}
                    blockedReasonId={blockedReasonId}
                    onAdd={() => onAdd(item)}
                    onDecrement={() => onDecrement(item.id)}
                    onRemove={() => onRemove(item.id)}
                  />
                </Grid>
              )
            })}
          </Grid>
        )}
      </Box>
      <Box aria-live="polite">
        {anyLineAtMax && (
          <Typography
            id={capPerItemId}
            variant="body3"
            color="mockup.neutral20"
          >
            {t("storeCharge.select.capPerItem")}
          </Typography>
        )}
      </Box>
    </Stack>
  )
}

export default SelectStep
